import {
  Code,
  ConnectError,
  createClient,
  type Interceptor,
} from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { IdentityService } from "@api-gen/identity_pb";
import { SportsService } from "@api-gen/sports_pb";
import { LiveService } from "@api-gen/live_pb";
import { ChatService } from "@api-gen/chat_pb";
import { PaymentService } from "@api-gen/payment_pb";
import { ContentService } from "@api-gen/content_pb";
import { NotificationService } from "@api-gen/notification_pb";

export const SESSION_MARKER_KEY = "live178.session";
export const DEVICE_ID_KEY = "live178.deviceId";

export function browserDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, created);
  return created;
}

export async function withPlaybackDeviceRecovery<T>(
  requestGrant: () => Promise<T>,
  revokeStaleSession: () => Promise<void>,
): Promise<T> {
  try {
    return await requestGrant();
  } catch (cause) {
    const error = ConnectError.from(cause);
    const staleDeviceSession =
      error.code === Code.PermissionDenied &&
      error.message
        .toLowerCase()
        .includes("playback grant device does not match the session");
    if (!staleDeviceSession) throw cause;

    // A retained HttpOnly cookie can outlive local browser storage. Revoke that
    // stale session before retrying as a guest; never weaken the API's device
    // binding check or silently attach the session to a different device.
    await revokeStaleSession();
    return requestGrant();
  }
}

export function defaultApiBaseUrl(
  hostname = window.location.hostname,
  origin = window.location.origin,
): string {
  const normalizedHost = hostname.trim().toLowerCase();
  if (normalizedHost.startsWith("live.")) {
    const parentDomain = normalizedHost.slice("live.".length);
    if (parentDomain.includes(".")) {
      const protocol = origin.startsWith("http://") ? "http" : "https";
      return `${protocol}://live-api.${parentDomain}`;
    }
  }
  return origin;
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? defaultApiBaseUrl();

const credentialedFetch: typeof globalThis.fetch = (input, init) =>
  globalThis.fetch(input, { ...init, credentials: "include" });

const refreshIdentity = createClient(
  IdentityService,
  createConnectTransport({ baseUrl, fetch: credentialedFetch }),
);
let refreshInFlight: Promise<void> | undefined;

const authentication: Interceptor = (next) => async (request) => {
  try {
    return await next(request);
  } catch (error) {
    const unauthenticated =
      ConnectError.from(error).code === Code.Unauthenticated;
    if (!unauthenticated || !localStorage.getItem(SESSION_MARKER_KEY)) {
      if (unauthenticated) {
        localStorage.removeItem(SESSION_MARKER_KEY);
      }
      throw error;
    }
    refreshInFlight ??= refreshIdentity
      .refreshSession({ deviceId: browserDeviceId() })
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = undefined;
      });
    try {
      await refreshInFlight;
      return await next(request);
    } catch {
      localStorage.removeItem(SESSION_MARKER_KEY);
      throw error;
    }
  }
};

const transport = createConnectTransport({
  baseUrl,
  fetch: credentialedFetch,
  interceptors: [authentication],
});

export const api = {
  identity: createClient(IdentityService, transport),
  sports: createClient(SportsService, transport),
  live: createClient(LiveService, transport),
  chat: createClient(ChatService, transport),
  payment: createClient(PaymentService, transport),
  content: createClient(ContentService, transport),
  notification: createClient(NotificationService, transport),
};

export function formatMoney(
  amountMinor: bigint | number,
  currency = "CNY",
): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(amountMinor) / 100);
}

export function formatCount(value: bigint | number): string {
  const number = Number(value);
  if (number >= 10_000)
    return `${(number / 10_000).toFixed(number >= 100_000 ? 0 : 1)}万`;
  return new Intl.NumberFormat("zh-CN").format(number);
}

export function readableError(error: unknown): string {
  const value = error instanceof Error ? error.message : String(error);
  return value.replace(/^\[[^\]]+\]\s*/, "") || "请求失败，请稍后再试";
}
